import React, {
  useEffect,
  useRef,
  useState,
  useImperativeHandle,
  RefObject,
  forwardRef
} from 'react';
import { Button, Intent } from '@blueprintjs/core';
import cloneDeep from 'lodash.clonedeep';
import { useTranslation } from 'react-i18next';
import Form, { FieldUpdateHandlerParam } from 'shared/form';
import * as types from 'data/sipSetting/sipSetting.types';
import { FormTemplate, FormTemplateSection } from 'data/template/template.types';
import {
  adaptSipSettingOperation,
  removeNewMoWithDeleteOperation,
  adaptToRemoveTemporaryId,
  getIncrementalId,
} from 'routes/SipSetting/utils/SipSetting.adapters';
import EmptyData from '../EmptyData';
import './network-elements-list-form.scss';

let idContoller = 1;

type Props = {
  data: types.SipNetworkElements[],
  template: FormTemplate,
  i18nKeyPrefix: string,
  isEditing?: boolean,
}

export type Ref = {
  isValid: (forceValidation: boolean, forceTouched: boolean) => boolean
  getValues: (keepOPeration: boolean) => types.SipNetworkElements[]
}

export const NetworkElementsListForm = forwardRef<Ref, Props>(({
  data,
  isEditing,
  template,
  i18nKeyPrefix,
}: Props, ref) => {

  const { t } = useTranslation();
  const [formData, setFormData] = useState<types.SipNetworkElements[]>([]);
  const [networkElementsTemplate, setNetworkElementsTemplate] = useState<FormTemplate>(template);
  const [deletedElements, setDeletedElements] = useState<string[]>([]);
  const [changedPrefixes, setChangedPrefixes] = useState<{ [key: string]: boolean }>({});
  const netElementsRefs = useRef<(RefObject<Form>)[]>([]);

  useEffect(() => {
    const clonedData = cloneDeep(data).map((item, index) => ({
      ...item,
    }));

    setFormData(clonedData);

    const clonedTemplate = cloneDeep(template);
    clonedTemplate.formId = Date.now().toString();
    setNetworkElementsTemplate(clonedTemplate);
  }, [data, template]);

  const isValid = () => {
    return netElementsRefs.current
      .filter((entry) => entry.current != null)
      .map((entry) => entry.current?.isValid(true, true) ?? true)
      .every((valid: boolean | undefined) => valid === true);
  }

  const getValues = (keepOperation: boolean) => {

    const netElementToDelete = deletedElements
      .map((id) => formData.find((item) => item.id === id))
      .filter((item) => item != null) as types.SipNetworkElements[];

    const netElementsValues = netElementsRefs.current
      .filter((entry) => entry.current != null)
      .map((entry: RefObject<Form>) => entry?.current?.getValues())
      .map((item) => adaptSipSettingOperation<types.SipNetworkElements>(item, changedPrefixes, keepOperation))
      .filter((item) => removeNewMoWithDeleteOperation<types.SipNetworkElements>(item))
      .map((item) => adaptToRemoveTemporaryId<types.SipNetworkElements>(item, keepOperation));

    return [...netElementsValues, ...netElementToDelete]
      .filter((item) => removeNewMoWithDeleteOperation<types.SipNetworkElements>(item));
  }

  useImperativeHandle(ref, () => ({
    isValid,
    getValues,
  }));

  const handleAddNetElements = () => {
    const clonedNetElements = cloneDeep(formData);
    const newData: types.SipNetworkElements = {
      id: `DX_NET_ID_${idContoller}`,
      nid: getIncrementalId<types.SipNetworkElements>(
        [...formData, ...data],
        'nid'
      ),
      netid: (networkElementsTemplate.networkElements as FormTemplateSection).fields.netid.initial as string,
      ipAddr: (networkElementsTemplate.networkElements as FormTemplateSection).fields.ipAddr.initial as string,
      port: (networkElementsTemplate.networkElements as FormTemplateSection).fields.port.initial as string,
      netype: (networkElementsTemplate.networkElements as FormTemplateSection).fields.netype.initial as string,
      operation: 'CREATE'
    }

    idContoller += 1;

    clonedNetElements.push(newData);
    setFormData(clonedNetElements);
  }

  const handleFieldUpdateHandler = (index: number) => ({ name, value }: FieldUpdateHandlerParam) => {
    const clonedData = cloneDeep(formData);

    const key = name as keyof types.SipNetworkElements;

    clonedData[index][key] = value;
    setFormData(clonedData);
    setChangedPrefixes((current) => ({ ...current, [clonedData[index].id]: true }));
  }

  const handleDeleteNetElement = (id: string) => () => {

    const clonedData = (cloneDeep(formData) as types.SipNetworkElements[])
      .map((item: types.SipNetworkElements, index) => {
      if (item.id === id) {
        setDeletedElements((current) => [...current, id])
        setChangedPrefixes((current) => ({ ...current, [id]: true }));

        return {
          ...item,
          operation: 'DELETE',
        }
      }

      return item;
    });

    setFormData(clonedData as types.SipNetworkElements[]);
  }

  const getRowData = (netData: types.SipNetworkElements[]) => {
    return netData.filter((item) => item.operation !== 'DELETE');
  }

  const renderNetElementsButton = (): JSX.Element | null => {

    if (!isEditing) {
      return null;
    }

    return (
      <Button
        data-testid="addDeviceButton"
        minimal
        intent={Intent.PRIMARY}
        icon="add"
        text={t('sipSetting.form.newNetElement')}
        onClick={handleAddNetElements}
      />
    );
  }

  const createNetElementsForm = (netData:types.SipNetworkElements, index: number) => {

    const netElementRef: { current: Form | null } = {
      current: null,
    };

    netElementsRefs.current.push(netElementRef);

    const refCallback = (node: Form | null) => {
      netElementRef.current = node;
    };

    return (
      <div key={netData.id as string} className="network-elements-list-form-row">
        <div className="network-elements-list-form-delete">
          {isEditing && (
            <Button icon="trash" minimal onClick={handleDeleteNetElement(netData.id)} />
          )}
        </div>
        <div data-testid="NetworkElementListForm" style={{ width: '100%' }}>
          <Form
            ref={refCallback}
            name={networkElementsTemplate.formId ?? networkElementsTemplate.key}
            defaults={netData}
            fields={(networkElementsTemplate.networkElements as FormTemplateSection).fields}
            layout={(networkElementsTemplate.networkElements as FormTemplateSection).metadata.layout?.rows}
            i18nKeyPrefix={i18nKeyPrefix}
            fieldUpdateHandler={handleFieldUpdateHandler(index)}
          />
        </div>
      </div>
    );
  }

  const renderNetElementsList = (): JSX.Element | null => {

    if (!getRowData(formData)?.length) {
      return <EmptyData message={t('sipSetting.form.noNetworkElement')} />
    }

    return (
      <div>
        <div className="network-elements-list-form-header">
          <div className="network-elements-list-form-header-item" />
          <div className="network-elements-list-form-header-item">
            {t('sipSetting.form.title.newType')}
          </div>
          <div className="network-elements-list-form-header-item">
            {t('sipSetting.form.title.ipAddress')}
          </div>
          <div className="network-elements-list-form-header-item">
            {t('sipSetting.form.title.port')}
          </div>
        </div>
        {
          getRowData(formData).map(createNetElementsForm).filter((entry) => entry != null)
        }
      </div>
    );
  }
  return (
    <div className="network-elements-list-form">
      {renderNetElementsButton()}
      {renderNetElementsList()}
    </div>
  )
});

export default NetworkElementsListForm;
